import { EKeyCode } from '@/enums/e_key_code';
import { assert_type_enum } from '@shared/asserts/utils_asserts';
import { Quaternion, Vector3 } from 'three';

export class OpenPoseFirstPersonKeyboardController {
  /*
   * FIELDS
   */
  public shift_pressed: boolean;

  public move_forward_pressed: boolean;
  public move_backward_pressed: boolean;

  public move_left_pressed: boolean;
  public move_right_pressed: boolean;

  public move_up_pressed: boolean;
  public move_down_pressed: boolean;

  public pitch_up_pressed: boolean;
  public pitch_down_pressed: boolean;

  public yaw_left_pressed: boolean;
  public yaw_right_pressed: boolean;

  private pressed_keys: Set<EKeyCode>;

  private on_key_down_bound = (keyboard_event: KeyboardEvent) => this.on_key_down(keyboard_event);
  private on_key_up_bound = (keyboard_event: KeyboardEvent) => this.on_key_up(keyboard_event);
  private on_window_blur_bound = () => this.reset_all_states();

  /*
   * METHODS
   */
  constructor() {
    this.shift_pressed = false;

    this.move_forward_pressed = false;
    this.move_backward_pressed = false;

    this.move_left_pressed = false;
    this.move_right_pressed = false;

    this.move_up_pressed = false;
    this.move_down_pressed = false;

    this.pitch_up_pressed = false;
    this.pitch_down_pressed = false;

    this.yaw_left_pressed = false;
    this.yaw_right_pressed = false;

    this.pressed_keys = new Set<EKeyCode>();
  }

  listeners_on() {
    window.addEventListener('keydown', this.on_key_down_bound, false);
    window.addEventListener('keyup', this.on_key_up_bound, false);
    window.addEventListener('blur', this.on_window_blur_bound, false);
  }

  listeners_off() {
    window.removeEventListener('keydown', this.on_key_down_bound, false);
    window.removeEventListener('keyup', this.on_key_up_bound, false);
    window.removeEventListener('blur', this.on_window_blur_bound, false);

    this.pressed_keys.clear();

    this.reset_all_states();
  }

  private on_key_down(event: KeyboardEvent) {
    assert_type_enum(event.code, EKeyCode);

    const code = event.code;
    this.pressed_keys.add(code);

    if (code === EKeyCode.ShiftLeft || code === EKeyCode.ShiftRight) this.shift_pressed = true;

    if (code === EKeyCode.W) this.move_forward_pressed = true;
    if (code === EKeyCode.S) this.move_backward_pressed = true;

    if (code === EKeyCode.A) this.move_left_pressed = true;
    if (code === EKeyCode.D) this.move_right_pressed = true;

    if (code === EKeyCode.Q) this.move_up_pressed = true;
    if (code === EKeyCode.E) this.move_down_pressed = true;

    if (code === EKeyCode.I) this.pitch_up_pressed = true;
    if (code === EKeyCode.K) this.pitch_down_pressed = true;

    if (code === EKeyCode.J) this.yaw_left_pressed = true;
    if (code === EKeyCode.L) this.yaw_right_pressed = true;
  }

  private on_key_up(event: KeyboardEvent) {
    assert_type_enum(event.code, EKeyCode);

    const code = event.code;
    this.pressed_keys.delete(code);

    if (code === EKeyCode.ShiftLeft || code === EKeyCode.ShiftRight) {
      this.shift_pressed = this.pressed_keys.has(EKeyCode.ShiftLeft) || this.pressed_keys.has(EKeyCode.ShiftRight);
    }

    if (code === EKeyCode.W) this.move_forward_pressed = false;
    if (code === EKeyCode.S) this.move_backward_pressed = false;

    if (code === EKeyCode.A) this.move_left_pressed = false;
    if (code === EKeyCode.D) this.move_right_pressed = false;

    if (code === EKeyCode.Q) this.move_up_pressed = false;
    if (code === EKeyCode.E) this.move_down_pressed = false;

    if (code === EKeyCode.I) this.pitch_up_pressed = false;
    if (code === EKeyCode.K) this.pitch_down_pressed = false;

    if (code === EKeyCode.J) this.yaw_left_pressed = false;
    if (code === EKeyCode.L) this.yaw_right_pressed = false;
  }

  reset_all_states() {
    this.shift_pressed = false;

    this.move_forward_pressed = false;
    this.move_backward_pressed = false;

    this.move_left_pressed = false;
    this.move_right_pressed = false;

    this.move_up_pressed = false;
    this.move_down_pressed = false;

    this.pitch_up_pressed = false;
    this.pitch_down_pressed = false;

    this.yaw_left_pressed = false;
    this.yaw_right_pressed = false;
  }

  is_movement_in_progress(): boolean {
    return this.move_forward_pressed || this.move_backward_pressed || this.move_left_pressed || this.move_right_pressed;
  }

  apply_movement_delta(world_quaternion: Quaternion, output_vector: Vector3): Vector3 {
    const forward_vector = new Vector3(0, 0, -1).applyQuaternion(world_quaternion).normalize();
    const right_vector = new Vector3(1, 0, 0).applyQuaternion(world_quaternion).normalize();
    const up_vector = new Vector3(0, 1, 0).applyQuaternion(world_quaternion).normalize();

    output_vector.set(0, 0, 0);

    if (this.move_forward_pressed) output_vector.add(forward_vector);
    if (this.move_backward_pressed) output_vector.sub(forward_vector);
    if (this.move_right_pressed) output_vector.add(right_vector);
    if (this.move_left_pressed) output_vector.sub(right_vector);
    if (this.move_up_pressed) output_vector.add(up_vector);
    if (this.move_down_pressed) output_vector.sub(up_vector);

    return output_vector;
  }

  calculate_rotation_pitch(look_speed_y: number) {
    let delta_pitch = 0;

    if (this.pitch_up_pressed) delta_pitch += look_speed_y * 0.02;
    if (this.pitch_down_pressed) delta_pitch -= look_speed_y * 0.02;

    return delta_pitch;
  }

  calculate_rotation_yaw(look_speed_x: number) {
    let delta_yaw = 0;

    if (this.yaw_left_pressed) delta_yaw += look_speed_x * 0.02;
    if (this.yaw_right_pressed) delta_yaw -= look_speed_x * 0.02;

    return delta_yaw;
  }
}
